/**
 * Drops a leading path segment the server did not recognise as an app route or a real theme, so a
 * typo'd or stale link resolves to the real page under the default theme instead of running the
 * whole session under a prefix that means nothing.
 *
 * The server owns the decision - it holds the only list of app routes - and reports it as
 * currentTheme.unrecognizedPathSegment. This runs before Angular bootstraps so the router's first
 * navigation sees the corrected URL.
 */
export function stripUnrecognizedThemeSegment(
  appConfig: any,
  location: Location = window.location,
  history: History = window.history,
): void {
  const segment: string = appConfig?.currentTheme?.unrecognizedPathSegment;
  if (!segment) return;

  // The URL written below is assembled from location's own already-normalised parts and always
  // starts with '/', so it cannot leave this origin whatever the server reported. The segment
  // only ever picks how many characters to drop - but it is echoed back from the request URL,
  // so it is held to the shape of a real path segment rather than trusted (Veracode 1334, 1351).
  if (!/^[\w.~%-]+$/.test(segment)) return;

  const prefix = `/${segment}`.toLowerCase();
  const pathname = location.pathname;
  const lower = pathname.toLowerCase();

  // The URL may have moved on since the config was requested; only strip what we were told about.
  if (lower !== prefix && !lower.startsWith(`${prefix}/`)) return;

  const remainder = pathname.slice(prefix.length) || '/';
  history.replaceState({}, '', `${remainder}${location.search}${location.hash}`);
}
